import { User, UserManager, type UserProfile, WebStorageStateStore } from 'oidc-client-ts'
import { cognitoConfig } from '@/lib/cognito-config'
import type { DirectAuthTokens } from './cognito-direct-auth'

// Bridges a directly-obtained Cognito session (SRP sign-in) into the exact
// session store `react-oidc-context`'s `<AuthProvider>` reads from (ADR-0011
// amendment). This is deliberately NOT a second session owner: it constructs
// a helper `UserManager` with the SAME authority/client_id/userStore settings
// `oidc-provider.tsx` uses, and calls the library's own `storeUser`, so the
// write lands under the identical storage key `<AuthProvider>`'s own
// UserManager reads on init. Every downstream consumer (`useAuth`,
// `ProtectedRoute`, logout, silent renew) stays completely unaware of which
// path produced the session — they only ever read the stored oidc-client-ts
// `User`, never how it got there.

function decodeIdTokenClaims(idToken: string): UserProfile {
	const payload = idToken.split('.')[1]
	if (!payload) throw new Error('Malformed ID token: missing payload segment')
	const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
	const json = decodeURIComponent(
		atob(base64)
			.split('')
			.map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
			.join(''),
	)
	return JSON.parse(json) as UserProfile
}

/** Same settings `oidc-provider.tsx`'s `buildAuthProviderSettings()` passes to `<AuthProvider>` — must match exactly so `storeUser` writes the key `<AuthProvider>`'s own UserManager will read. */
function buildHelperUserManager(): UserManager {
	if (!cognitoConfig) throw new Error('storeDirectSession called without a valid Cognito configuration')
	return new UserManager({
		authority: cognitoConfig.authority,
		client_id: cognitoConfig.clientId,
		redirect_uri: cognitoConfig.redirectUri,
		scope: cognitoConfig.scope,
		response_type: 'code',
		userStore: new WebStorageStateStore({ store: window.sessionStorage }),
	})
}

/**
 * Persists `tokens` as the active oidc-client-ts session, then hard-navigates
 * to `destination`. A full navigation (not an SPA route change) is deliberate:
 * it remounts `<AuthProvider>`, whose own `UserManager` then reads the
 * just-stored user on init — the same mechanism that already makes the
 * Managed Login redirect round-trip work today, reused rather than duplicated
 * with a live event-bridge.
 */
export async function storeDirectSessionAndNavigate(tokens: DirectAuthTokens, destination: string): Promise<void> {
	const user = new User({
		id_token: tokens.idToken,
		access_token: tokens.accessToken,
		refresh_token: tokens.refreshToken,
		token_type: 'Bearer',
		scope: cognitoConfig?.scope,
		profile: decodeIdTokenClaims(tokens.idToken),
		expires_at: Math.floor(Date.now() / 1000) + tokens.expiresIn,
	})
	const userManager = buildHelperUserManager()
	await userManager.storeUser(user)
	window.location.assign(destination)
}
